/**
 * PI-RADS scoring annotation extension handler.
 *
 * Stores per-lesion PI-RADS v2.1 fields in the generic EAV model.
 * Each annotation row is linked to a mask instance via linked_annotation_id.
 */
import { PIRADS_ALL_FIELDS, PIRADS_INT_FIELDS, PIRADS_TEXT_FIELDS } from "@shared/lib/pirads-types.ts"

import { BaseExtensionHandler } from "../_base.ts"
import type {
  ClientValidationContext,
  CompletionValidationContext,
  ExtensionManifest,
  SerializedAnnotation,
  StoredAnnotation
} from "../_interface.ts"
import {
  createPiradsSchema,
  PIRADS_FIELD_LABELS,
  type PiradsRequirableField,
  piradsConfigSchema,
  piradsProjectConfigSchema,
  resolvePiradsRequiredFields,
  updatePiradsSchema
} from "./schema.ts"

/**
 * The client workspace stores PI-RADS values in ScoreAnnotationState.values using
 * camelCase keys (`T2Score`, `DWIScore`, …). Completion requirements are declared
 * in snake_case EAV form. Keep this map in sync with the panel's `piradsToValues`.
 */
const PIRADS_SNAKE_TO_CLIENT_KEY: Record<PiradsRequirableField, string> = {
  laterality: "laterality",
  level: "level",
  zones: "zones",
  t2_score: "T2Score",
  t2_reason: "T2Reason",
  t2_up_down_grade: "T2UpDownGrade",
  dwi_score: "DWIScore",
  dwi_reason: "DWIReason",
  dwi_up_down_grade: "DWIUpDownGrade",
  dce_score: "DCEScore",
  dce_reason: "DCEReason",
  isup_grade: "ISUPGrade"
}

interface PiradsPayload {
  linked_annotation_id?: string | null
  laterality?: string
  level?: string
  zones?: string[]
  t2_score?: number | ""
  t2_reason?: string
  t2_up_down_grade?: string
  dwi_score?: number | ""
  dwi_reason?: string
  dwi_up_down_grade?: string
  dce_score?: string
  dce_reason?: string
  isup_grade?: string
}

export class PiradsHandler extends BaseExtensionHandler {
  readonly type = "pirads"
  readonly manifest: ExtensionManifest = {
    type: "pirads",
    label: "PI-RADS",
    scope: "case",
    storage: "structured",
    supportsLinking: true
  }

  override getConfigSchema() {
    return piradsConfigSchema
  }

  override getProjectConfigSchema() {
    return piradsProjectConfigSchema
  }

  getCreateSchema() {
    return createPiradsSchema
  }

  getUpdateSchema() {
    return updatePiradsSchema
  }

  serializeForDb(apiData: unknown): SerializedAnnotation {
    const payload = apiData as PiradsPayload
    const dataRows: SerializedAnnotation["data"] = []

    // Text fields
    for (const field of PIRADS_TEXT_FIELDS) {
      const value = (payload as Record<string, unknown>)[field]
      if (value !== undefined && value !== null) {
        // zones is stored as JSON array in value_text
        const textValue = field === "zones" ? JSON.stringify(value) : String(value)
        dataRows.push({
          field_key: field,
          value_text: textValue
        })
      }
    }

    // Integer fields (t2_score, dwi_score)
    for (const field of PIRADS_INT_FIELDS) {
      const value = (payload as Record<string, unknown>)[field]
      if (value !== undefined && value !== null && value !== "") {
        dataRows.push({
          field_key: field,
          value_int: Number(value)
        })
      }
    }

    return {
      annotation: {
        linked_annotation_id: payload.linked_annotation_id ?? null
      },
      data: dataRows,
      files: []
    }
  }

  deserializeFromDb(stored: StoredAnnotation): unknown {
    const result: Record<string, unknown> = {
      id: stored.annotation.id,
      submission_id: stored.annotation.submission_id,
      project_module_id: stored.annotation.project_module_id,
      series_id: stored.annotation.series_id,
      linked_annotation_id: stored.annotation.linked_annotation_id,
      created_at: stored.annotation.created_at,
      updated_at: stored.annotation.updated_at
    }

    for (const d of stored.data) {
      if (!PIRADS_ALL_FIELDS.includes(d.field_key)) continue

      if ((PIRADS_INT_FIELDS as readonly string[]).includes(d.field_key)) {
        result[d.field_key] = d.value_int ?? ""
      } else if (d.field_key === "zones" && d.value_text) {
        try {
          result[d.field_key] = JSON.parse(d.value_text)
        } catch {
          result[d.field_key] = []
        }
      } else {
        result[d.field_key] = d.value_text ?? ""
      }
    }

    return result
  }

  getExportColumns(_templateSchema: unknown): string[] {
    return [...PIRADS_ALL_FIELDS]
  }

  toExportRow(annotations: StoredAnnotation[], _templateSchema: unknown): Record<string, string | number> {
    const result: Record<string, string | number> = {}

    for (const field of PIRADS_ALL_FIELDS) {
      result[field] = ""
    }

    // For PI-RADS, each annotation is per-lesion. Concatenate lesion data with pipe separator.
    const sortedAnnotations = [...annotations].sort((a, b) =>
      a.annotation.created_at.localeCompare(b.annotation.created_at)
    )

    for (const annotation of sortedAnnotations) {
      for (const d of annotation.data) {
        if (!PIRADS_ALL_FIELDS.includes(d.field_key)) continue

        let value: string | number = ""
        if ((PIRADS_INT_FIELDS as readonly string[]).includes(d.field_key)) {
          value = d.value_int ?? ""
        } else {
          value = d.value_text ?? ""
        }

        const existing = result[d.field_key]
        if (existing === "" || existing === null || existing === undefined) {
          result[d.field_key] = value
        } else {
          result[d.field_key] = `${String(existing)}|${String(value)}`
        }
      }
    }

    return result
  }

  override validateForCompletion(
    annotations: StoredAnnotation[],
    _templateSchema: unknown,
    context: CompletionValidationContext
  ): { valid: boolean; errors: string[] } {
    const required = resolvePiradsRequiredFields(context.projectModuleConfigJson)
    const errors: string[] = []

    // Collect lesion sources via instance_linked dependencies.
    const lesionLabelById = new Map<string, string>()
    const allSourceAnnotations: StoredAnnotation[] = []
    for (const dep of context.dependencies) {
      if (dep.dependency_mode !== "instance_linked") continue
      for (const src of dep.sourceAnnotations) {
        allSourceAnnotations.push(src)
        lesionLabelById.set(src.annotation.id, formatLesionLabel(src))
      }
    }

    // Index PIRADS rows by linked lesion id.
    const rowsByLesion = new Map<string, StoredAnnotation[]>()
    const orphanRows: StoredAnnotation[] = []
    for (const row of annotations) {
      const linkId = row.annotation.linked_annotation_id
      if (linkId) {
        const list = rowsByLesion.get(linkId) ?? []
        list.push(row)
        rowsByLesion.set(linkId, list)
      } else {
        orphanRows.push(row)
      }
    }

    if (allSourceAnnotations.length > 0) {
      for (const lesion of allSourceAnnotations) {
        const lesionLabel = lesionLabelById.get(lesion.annotation.id) ?? "Lesion"
        const rows = rowsByLesion.get(lesion.annotation.id) ?? []
        if (rows.length === 0) {
          errors.push(`Lesion '${lesionLabel}': no PI-RADS score entered`)
          continue
        }
        for (const row of rows) {
          for (const fieldKey of findMissingPiradsFields(row, required)) {
            errors.push(`Lesion '${lesionLabel}': ${PIRADS_FIELD_LABELS[fieldKey]} is missing`)
          }
        }
      }
    }

    for (const row of orphanRows) {
      for (const fieldKey of findMissingPiradsFields(row, required)) {
        errors.push(`PI-RADS row: ${PIRADS_FIELD_LABELS[fieldKey]} is missing`)
      }
    }

    return { valid: errors.length === 0, errors }
  }

  override validateCompletionClient(context: ClientValidationContext): { valid: boolean; errors: string[] } {
    const { module, modules, masks, scores } = context
    const required = resolvePiradsRequiredFields(module.config)
    const errors: string[] = []

    // Resolve instance-linked mask dependency + its lesions.
    const linked = findLinkedMaskDependency(module, modules)
    const lesions = linked
      ? masks.filter((m) => {
          if (m.projectModuleId !== linked.moduleId) return false
          if (linked.labelFilter && m.labelName && !linked.labelFilter.includes(m.labelName)) return false
          return true
        })
      : []

    if (lesions.length === 0) {
      // No lesions → validate any orphan rows that were created anyway.
      for (const score of scores) {
        if (score.projectModuleId !== module.id) continue
        for (const fieldKey of findMissingPiradsClientFields(score.values, required)) {
          errors.push(`PI-RADS row: ${PIRADS_FIELD_LABELS[fieldKey]} is missing`)
        }
      }
      return { valid: errors.length === 0, errors }
    }

    for (const lesion of lesions) {
      const label = lesion.labelName
        ? `${lesion.labelName} ${lesion.instanceOrdinal}`
        : `Lesion ${lesion.instanceOrdinal}`
      const score = scores.find((s) => s.projectModuleId === module.id && s.labelName === lesion.id)
      if (!score) {
        errors.push(`Lesion '${label}': no PI-RADS score entered`)
        continue
      }
      for (const fieldKey of findMissingPiradsClientFields(score.values, required)) {
        errors.push(`Lesion '${label}': ${PIRADS_FIELD_LABELS[fieldKey]} is missing`)
      }
    }

    return { valid: errors.length === 0, errors }
  }
}

// ---------------------------------------------------------------------------
// Client-side helpers
// ---------------------------------------------------------------------------

interface LinkedMaskInfo {
  moduleId: string
  labelFilter: readonly string[] | null
}

function findLinkedMaskDependency(
  module: ClientValidationContext["module"],
  modules: ClientValidationContext["modules"]
): LinkedMaskInfo | null {
  for (const dep of module.dependencies) {
    if (dep.dependency_mode !== "instance_linked") continue
    const target = modules.find((m) => m.id === dep.depends_on_module_id)
    if (target?.template_type === "mask") {
      return {
        moduleId: target.id,
        labelFilter: dep.source_label_names && dep.source_label_names.length > 0 ? dep.source_label_names : null
      }
    }
  }
  return null
}

function findMissingPiradsClientFields(
  values: Record<string, string | number | boolean | null>,
  required: readonly PiradsRequirableField[]
): PiradsRequirableField[] {
  const missing: PiradsRequirableField[] = []
  for (const field of required) {
    const raw = values[PIRADS_SNAKE_TO_CLIENT_KEY[field]]

    if (field === "t2_score" || field === "dwi_score") {
      const v = typeof raw === "number" ? raw : Number(raw)
      if (!Number.isFinite(v) || v < 1 || v > 5) missing.push(field)
      continue
    }
    if (field === "dce_score") {
      if (raw !== "neg" && raw !== "pos") missing.push(field)
      continue
    }
    if (field === "zones") {
      let arr: unknown = raw
      if (typeof raw === "string") {
        try {
          arr = JSON.parse(raw)
        } catch {
          arr = null
        }
      }
      if (!Array.isArray(arr) || arr.length === 0) missing.push(field)
      continue
    }
    if (typeof raw !== "string" || raw.length === 0) missing.push(field)
  }
  return missing
}

// ---------------------------------------------------------------------------
// Server-side helpers (exported for tests)
// ---------------------------------------------------------------------------

function formatLesionLabel(src: StoredAnnotation): string {
  const name = src.annotation.label_name ?? "Lesion"
  // Lesion instance ordinals are not stored on the annotations table directly;
  // surface a stable but human-readable identifier using label name + id suffix.
  return name
}

export function findMissingPiradsFields(
  row: StoredAnnotation,
  required: readonly PiradsRequirableField[]
): PiradsRequirableField[] {
  const byKey = new Map<string, { value_text: string | null; value_int: number | null }>()
  for (const d of row.data) {
    byKey.set(d.field_key, { value_text: d.value_text, value_int: d.value_int })
  }

  const missing: PiradsRequirableField[] = []
  for (const field of required) {
    const entry = byKey.get(field)
    if (!entry) {
      missing.push(field)
      continue
    }
    if ((PIRADS_INT_FIELDS as readonly string[]).includes(field)) {
      const v = entry.value_int
      if (v === null || v === undefined || v < 1 || v > 5) missing.push(field)
      continue
    }
    if (field === "dce_score") {
      const v = entry.value_text ?? ""
      if (v !== "neg" && v !== "pos") missing.push(field)
      continue
    }
    if (field === "zones") {
      const v = entry.value_text ?? ""
      if (!v) {
        missing.push(field)
        continue
      }
      try {
        const parsed = JSON.parse(v)
        if (!Array.isArray(parsed) || parsed.length === 0) missing.push(field)
      } catch {
        missing.push(field)
      }
      continue
    }
    // Generic text field: non-empty required.
    const v = entry.value_text ?? ""
    if (!v) missing.push(field)
  }
  return missing
}
